import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Searchbar,
  Chip,
  Button,
  Text,
  Menu,
  useTheme,
  Switch,
} from 'react-native-paper';
import { useDebounce } from '@coffeeplexity/shared';
import { Filter, ChevronDown } from 'lucide-react-native';

export interface EquipmentSearchProps {
  onParamsChange: (params: any) => void;
  initialParams?: any;
}

const sortOptions = [
  { key: 'created_at-desc', label: 'Newest First' },
  { key: 'created_at-asc', label: 'Oldest First' },
  { key: 'name-asc', label: 'Name A-Z' },
  { key: 'name-desc', label: 'Name Z-A' },
  { key: 'price_min-asc', label: 'Price: Low to High' },
  { key: 'price_min-desc', label: 'Price: High to Low' },
  { key: 'rating_average-desc', label: 'Highest Rated' },
];

const categories = [
  'grinders',
  'espresso_machines',
  'brewing_equipment',
  'scales',
  'accessories',
  'filters',
  'storage',
];

export function EquipmentSearch({
  onParamsChange,
  initialParams,
}: EquipmentSearchProps) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState(initialParams?.search || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [filters, setFilters] = useState({
    category: initialParams?.filters?.category || [],
    price_min: initialParams?.filters?.price_min || undefined,
    price_max: initialParams?.filters?.price_max || undefined,
    is_featured: initialParams?.filters?.is_featured || undefined,
    min_rating: initialParams?.filters?.min_rating || undefined,
  });

  const [sortBy, setSortBy] = useState(initialParams?.sort_by || 'created_at');
  const [sortOrder, setSortOrder] = useState(
    initialParams?.sort_order || 'desc'
  );

  const debouncedSearch = useDebounce(searchQuery, 300);

  const updateParams = useCallback(() => {
    const params = {
      search: debouncedSearch || undefined,
      filters: {
        category: filters.category.length > 0 ? filters.category : undefined,
        price_min: filters.price_min,
        price_max: filters.price_max,
        is_featured: filters.is_featured,
        min_rating: filters.min_rating,
      },
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    // Remove empty filters
    if (
      Object.values(params.filters || {}).every(
        v => v === undefined || (Array.isArray(v) && v.length === 0)
      )
    ) {
      delete params.filters;
    }

    onParamsChange(params);
  }, [debouncedSearch, filters, sortBy, sortOrder, onParamsChange]);

  useEffect(() => {
    updateParams();
  }, [updateParams]);

  const handleCategoryToggle = (category: string) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category],
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: [],
      price_min: undefined,
      price_max: undefined,
      is_featured: undefined,
      min_rating: undefined,
    });
  };

  const hasActiveFilters = Object.values(filters).some(
    v => v !== undefined && (!Array.isArray(v) || v.length > 0)
  );

  const currentSortLabel =
    sortOptions.find(opt => opt.key === `${sortBy}-${sortOrder}`)?.label ||
    'Newest First';

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      <Searchbar
        placeholder='Search equipment...'
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={[styles.searchBar, { backgroundColor: theme.colors.surface }]}
        iconColor={theme.colors.onSurfaceVariant}
      />

      {/* Filter and Sort Controls */}
      <View style={styles.controls}>
        <Button
          mode={showFilters || hasActiveFilters ? 'contained' : 'outlined'}
          onPress={() => setShowFilters(!showFilters)}
          icon={({ size, color }) => <Filter size={size} color={color} />}
          style={styles.filterButton}
          contentStyle={styles.buttonContent}
        >
          Filters {hasActiveFilters && '•'}
        </Button>

        <Menu
          visible={showSortMenu}
          onDismiss={() => setShowSortMenu(false)}
          anchor={
            <Button
              mode='outlined'
              onPress={() => setShowSortMenu(true)}
              icon={({ size, color }) => (
                <ChevronDown size={size} color={color} />
              )}
              contentStyle={styles.buttonContent}
            >
              {currentSortLabel}
            </Button>
          }
        >
          {sortOptions.map(option => (
            <Menu.Item
              key={option.key}
              onPress={() => {
                const [newSortBy, newSortOrder] = option.key.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
                setShowSortMenu(false);
              }}
              title={option.label}
            />
          ))}
        </Menu>
      </View>

      {/* Filters Panel */}
      {showFilters && (
        <View
          style={[
            styles.filtersPanel,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <ScrollView style={styles.filtersContent}>
            {/* Categories */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.filterTitle}>
                Categories
              </Text>
              <View style={styles.chipsContainer}>
                {categories.map(category => (
                  <Chip
                    key={category}
                    selected={filters.category.includes(category)}
                    onPress={() => handleCategoryToggle(category)}
                    style={styles.filterChip}
                  >
                    {category
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </Chip>
                ))}
              </View>
            </View>

            {/* Price Range */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.filterTitle}>
                Price Range
              </Text>
              <View style={styles.priceInputs}>
                <View style={styles.priceInputContainer}>
                  <Text variant='labelMedium'>Min ($)</Text>
                  {/* Note: In production, you'd want proper TextInput components */}
                  <Text variant='bodySmall' style={styles.priceNote}>
                    Price filtering coming soon
                  </Text>
                </View>
              </View>
            </View>

            {/* Minimum Rating */}
            <View style={styles.filterSection}>
              <Text variant='titleSmall' style={styles.filterTitle}>
                Minimum Rating
              </Text>
              <View style={styles.ratingFilters}>
                {[4.0, 4.5, 4.7].map(rating => (
                  <Chip
                    key={rating}
                    selected={filters.min_rating === rating}
                    onPress={() =>
                      setFilters(prev => ({
                        ...prev,
                        min_rating:
                          prev.min_rating === rating ? undefined : rating,
                      }))
                    }
                    style={styles.filterChip}
                  >
                    {rating}+ ⭐
                  </Chip>
                ))}
              </View>
            </View>

            {/* Featured Toggle */}
            <View style={styles.filterSection}>
              <View style={styles.switchRow}>
                <Text variant='bodyMedium'>Featured equipment only</Text>
                <Switch
                  value={filters.is_featured === true}
                  onValueChange={value =>
                    setFilters(prev => ({
                      ...prev,
                      is_featured: value ? true : undefined,
                    }))
                  }
                />
              </View>
            </View>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                mode='outlined'
                onPress={clearFilters}
                style={styles.clearButton}
              >
                Clear All Filters
              </Button>
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
  },
  searchBar: {
    marginHorizontal: 16,
    marginVertical: 6,
    elevation: 2,
    height: 52,
    borderRadius: 12,
  },
  controls: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 12,
  },
  filterButton: {
    flex: 1,
  },
  buttonContent: {
    height: 40,
  },
  filtersPanel: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    elevation: 2,
    maxHeight: 400,
  },
  filtersContent: {
    padding: 16,
  },
  filterSection: {
    marginBottom: 20,
  },
  filterTitle: {
    marginBottom: 12,
    fontWeight: '600',
  },
  chipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    marginBottom: 4,
  },
  priceInputs: {
    flexDirection: 'row',
    gap: 12,
  },
  priceInputContainer: {
    flex: 1,
  },
  priceNote: {
    opacity: 0.7,
    fontStyle: 'italic',
  },
  ratingFilters: {
    flexDirection: 'row',
    gap: 8,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  clearButton: {
    marginTop: 8,
  },
});
